"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createInlinerEventBus = exports.onInlinerEventStatus = exports.pollForInitialRequestInlineLoggerStatus = void 0;
const common_1 = require("./common");
const requestInlineLoggerStatus = () => new Promise((res) => {
    (0, common_1.once)(common_1.EventTypeInlinerAck, (event) => {
        return res(event);
    });
    (0, common_1.emitStatusRequestEvent)();
});
/**
 * @info We have a pub/sub event bus model for communicating with the
 * inlined code, and we're also dealing with a call/response model (e.g., consumer calls to
 * inliner, inliner responds).
 *
 * As long as we know that the inliner has been initialized first, we would always expect
 * to be able to wait until the inliner has responded. But because we're dealing with
 * concurrency at the browser level, assume that it's possible to have started the consumer
 * before the inlined code is not ready or has not subscribed to the call event.
 *
 * This function provides a polling mechanism to retry on timeout some number of times
 * before giving up. It gives inliner code flexibility in implementation. We don't have to
 * assume the direct ordering of events. Instead we say, as long as the inliner is initialized
 * as some reasonable interval after.
 */
const pollForInitialRequestInlineLoggerStatus = ({ retries, timeoutMs, }) => (0, common_1.retry)(() => (0, common_1.timeout)(requestInlineLoggerStatus, "RequestInlineLoggerStatus timeout.", timeoutMs), retries);
exports.pollForInitialRequestInlineLoggerStatus = pollForInitialRequestInlineLoggerStatus;
/**
 * @info Because this api uses an event bus to communicate, the order of operations
 * for subscribing to the event matters.
 *
 * There are several possible outcomes:
 *
 * - It could be that the inline library failed a long time ago
 * - It could be that the inline library is in the process of failing and we'll miss subscribing to the event
 * - It could be that the inline library timed out and never loaded
 *
 * The solution is to observe all events going through the bus, and terminate once we've reached a terminating state.
 * Then we kick off an initial ack / response. Now, if the inliner has settled before we get to initialize here,
 * we'll get it's last status.
 */
const onInlinerEventStatus = () => new Promise((resolve, reject) => {
    (0, common_1.onUntil)(common_1.EventTypeInlinerAck, (e) => {
        if (e.detail.status === "Failure")
            reject(e.detail.error);
        if (e.detail.status === "Success" || e.detail.status === "Preload")
            resolve(e.detail);
        return {
            done: e.detail.status !== "Initialize",
            value: null,
        };
    });
    // It should be reasonable to constrain this to a short amount of time
    // If the inliner is blocking for longer than 150 ms, then that should
    // be considered a failure path.
    (0, exports.pollForInitialRequestInlineLoggerStatus)({
        retries: 3,
        timeoutMs: 50,
    }).catch(reject);
});
exports.onInlinerEventStatus = onInlinerEventStatus;
/**
 * @info this interface initializes an event bus for the inliner code to send back a logger
 * instance or communicate to the consumer that the inliner failed in some way.
 *
 * It allows for the logger code to be inlined into the document, while giving consumers
 * the freedom to wait until the logger is ready, know if it failed, or defer offloading logs
 * until it has started.
 */
const createInlinerEventBus = () => {
    let inlinerResponse = { status: "Initialize" };
    const listener = () => (0, common_1.emitAckResponse)(inlinerResponse);
    window.addEventListener(common_1.EventTypeInlinerStatusRequest, listener);
    return {
        updateInlinerState: (state) => {
            inlinerResponse = state;
            (0, common_1.emitAckResponse)(inlinerResponse);
        },
        dispose: () => window.removeEventListener(common_1.EventTypeInlinerStatusRequest, listener),
    };
};
exports.createInlinerEventBus = createInlinerEventBus;
